import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";

export default function VerifyEmail() {
  const { session, emailVerified, refresh } = useAuth();
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (emailVerified) navigate("/app");
  }, [emailVerified, navigate]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const { data } = await supabase.auth.refreshSession();
      if (data.user?.email_confirmed_at) {
        await refresh();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const resend = async () => {
    if (!session?.user?.email) return;
    setSending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: session.user.email,
      options: { emailRedirectTo: `${window.location.origin}/verify-email` },
    });
    setSending(false);
    if (error) return toast.error(error.message);
    toast.success("Verification email resent");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] p-6">
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#E8E2D9] p-10 text-center" data-testid="verify-email-page">
        <div className="w-14 h-14 mx-auto mb-6 rounded-2xl bg-[#FDE9D8] flex items-center justify-center">
          <MailCheck size={26} className="text-[#E8712C]" strokeWidth={1.5} />
        </div>
        <h1 className="font-display font-bold text-2xl tracking-tight mb-2">Verify your email</h1>
        <p className="text-sm text-[#57534E] leading-relaxed mb-6">
          We've sent a verification link to <br />
          <b className="text-[#1C1917]">{session?.user?.email || "your inbox"}</b>.
          <br />Click the link to activate your account. This page will refresh automatically.
        </p>
        <Button data-testid="resend-email-btn" onClick={resend} disabled={sending} variant="outline" className="rounded-full">
          {sending ? "Sending…" : "Resend email"}
        </Button>
        <div className="mt-6 text-xs text-[#A8A29E]">
          Wrong email? <Link to="/signup" className="text-[#E8712C]">Sign up again</Link>
        </div>
      </div>
    </div>
  );
}
